/**
 * 再現エンジンのデータ取得（Issue #497）。サーバーの Ocg* API（`api.ocg*`）を呼ぶのはこのファイルだけ。
 * replay の解決・状態の取得・応答・取り消し・replay の作成/やり直しを持ち、
 * `replay_id` / `want_events: true` / `force` の付け方をここに閉じる（ページは api と replay_id を知らない）。
 *
 * 書き込み系は `{ ok, status, body }` をそのまま返す（`body.force_required` が立っていれば force で書けば通る 400。Issue #437）。
 * `hold`（登録待ち。Issue #612）を立てると `hold_unconfirmed: true` を付け、最後の応答より後ろで未確認のカードが使われたら
 * サービスがそのイベントで止めて `body.hold` を返す（pending は無い）。入力画面だけが立てる（演出の画面・タイミング調節は今までどおり問い合わせまで進める）。
 */
import { api } from '../../api/api';
import { deckIdsOfGame } from '../../utils/gameInfo';

/** 試合の replay の一覧（新しい順。API の返しが配列でも {data} でも配列に） */
export async function listReplays(gameUuid) {
  const list = await api.ocgListReplays(gameUuid);
  return Array.isArray(list) ? list : (list?.data ?? []);
}

/** 一覧から開く replay（1 試合 1 replay なので先頭＝一番新しいもの。無ければ null）。wanted（URL の ?replay=）があればそれを優先 */
export function latestReplayId(rows, wanted = null) {
  const w = Number(wanted);
  return rows.find((r) => r.replay_id === w)?.replay_id ?? rows[0]?.replay_id ?? null;
}

/**
 * replay の状態（`OcgGetState` を先頭から再実行した結果。events / board_steps / injections / board / pending / stopped）と
 * 一次データ（`OcgGetReplay`。replay の head・ops・injections）。状態が取れなければ throw
 */
/**
 * 入力の見え方（Issue #663 / #665）: 状態を返す呼び出しには、サーバーが作る `input`（今答える問い合わせ・選択肢・登録の一覧・日本語の文）を付けてもらう。
 * 問い合わせの読み方と答え方はサーバーが持つ（画面は input を描くだけ。答えは `answer`）。forced でないチェーン確認は kind auto（Issue #658）
 */
const INPUT = { want_input: true, auto_chains: true };

export async function loadReplay(replayId, { hold = false } = {}) {
  const [st, replay] = await Promise.all([api.ocgGetState(replayId, { want_events: true, ...holdOf(hold), ...INPUT }), api.ocgGetReplay(replayId)]);
  if (!st?.ok) throw new Error(st?.body?.error ?? '再現エンジンの状態を取れませんでした');
  return { state: st.body, replay: replay ?? null };
}

/** replay の一次データだけ（head・ops・injections。`OcgGetReplay`）。応答のあとに ops を取り直すときに使う */
export function getReplay(replayId) {
  return api.ocgGetReplay(replayId);
}

const forceOf = (force) => (force ? { force: true } : {});
const holdOf = (hold) => (hold ? { hold_unconfirmed: true } : {});

/**
 * 今の問い合わせに答える（videoMs = 答えた時刻。事実として記録され、編集では動かさない）。
 * autoPass（Issue #658）: 渡すと、この応答のあとのチェーン確認をサービスが決まりどおりに通して印付きで書く（`AUTO_PASS_ALL` など）。
 * asAuto: この応答自体も自動で通したものとして印を付ける（「チェーンしない」を画面が自動で送るとき）
 */
/**
 * 今の問い合わせ（state.input.query）か登録の一覧の項目に答える（OcgAnswer）。answer: { id, picks } / { id, value } / { register_key, pick | code | value | picks | confirm }。
 * サーバーが core への応答・登録・開始に直して書き、書いたあとの状態（input 付き）を返す
 */
export function answer(replayId, ans, { videoMs = null, autoPass = null, asAuto = false, force = false, seat = null, expectedRev = null } = {}) {
  return api.ocgAnswer({ replay_id: replayId, answer: ans, video_ms: videoMs, ...(autoPass ? { auto_pass: autoPass } : {}), ...(asAuto ? { as_auto: true } : {}),
    ...forceOf(force), want_events: true, auto_chains: true,
    // 席で答える（タブレット。Issue #711）: サーバーが答える番か・自分のドローかを見て、版（expected_rev）が古ければ 400 stale。返りは席向けの形（seatView で直す）
    ...(seat != null ? { seat, expected_rev: expectedRev } : {}) });
}

/**
 * 席で読む（OcgSeatState。タブレット。Issue #711）。両席に同じものが届き、誰に見えるかは盤面・選択肢の visible_to の印（隠すのは画面）。
 * sinceRev が今の版と同じなら { changed: false, rev } だけ（毎秒のポーリング）
 */
export async function loadSeat(replayId, seat, sinceRev = null) {
  // seat が null は席なしの観戦（見る専用がルームが開いているかを見る。Issue #765）
  const r = await api.duelResult('OcgSeatState', { replay_id: replayId, ...(seat != null ? { seat } : {}), ...(sinceRev != null ? { since_rev: sinceRev } : {}) });
  if (!r?.ok) throw new Error(r?.body?.error ?? '再現エンジンの状態を取れませんでした');
  return seatView(r.body);
}

/**
 * 席向けの返り（OcgSeatState / 席つきの OcgAnswer・OcgSeatUndo / ルームの state）→ { changed, rev, state（input 付き）, replay, progress, room }。
 * room: ルームが開いている試合なら本番が付ける入口 { room_id, ws_url, key, seat }（Issue #764 / #765。無ければ null＝今のポーリング）
 */
export function seatView(body) {
  // progress: 選んでいる途中（2 席で共有。Issue #717）。版が変わらない軽い返りにも載る
  const room = body?.room ?? null;
  if (!body || body.changed === false) return { changed: false, rev: body?.rev ?? null, progress: body?.progress ?? null, room };
  return { changed: true, rev: body.rev ?? null, state: { ...(body.state ?? {}), input: body.input ?? null }, replay: body.replay ?? null, progress: body.progress ?? null, room };
}

/** 選んでいる途中を足す・外す・空にする（2 席で共有。OcgSeatProgress。Issue #717）。op: add / remove / clear */
export function seatProgress(replayId, seat, queryId, op, { index = null, allSeats = false } = {}) {
  return api.duelResult('OcgSeatProgress', { replay_id: replayId, seat, query_id: queryId, op,
    ...(index != null ? { index } : {}), ...(allSeats ? { all_seats: true } : {}) });
}

/** 席で戻す（最後の人の答えまで。どちらの席から押しても。OcgSeatUndo） */
export function seatUndo(replayId, seat, expectedRev) {
  return api.duelResult('OcgSeatUndo', { replay_id: replayId, seat, expected_rev: expectedRev });
}

export function rewindTo(replayId, seq, opCount, { hold = false } = {}) {
  return undo(replayId, Math.max(0, opCount - seq), { hold });
}

/** 末尾の応答を count 手戻す */
export function undo(replayId, count = 1, { hold = false } = {}) {
  return api.ocgUndo(replayId, count, { ...holdOf(hold), want_events: true, ...INPUT });
}

/**
 * デッキレシピの今の中身を replay の写しへ取り込む（`OcgSyncDeck`。Issue #336 / #618）。deckIds = [P1, P2]（試合のデッキ。utils/gameInfo の deckIdsOfGame）。
 * rematch: 記録済みの応答を選んだカードで付け直して確かめる（デッキの中身が変わると、デッキから選ぶ問い合わせの候補の並びがずれるため）。
 * body.changed が false なら何も変わっていない（状態は返らない）。記録が止まるなら 400 で truncate_required（止まる手 stopped・
 * 消える手の数 drop_ops）。truncate: 止まる手から先の記録を消して取り込む（強制して反映は無い。Issue #954）
 */
export function syncDeck(replayId, deckIds, { rematch = true, truncate = false } = {}) {
  const [deck1, deck2] = deckIds ?? [];
  return api.ocgSyncDeck({ replay_id: replayId, deck1_id: deck1 ?? null, deck2_id: deck2 ?? null, rematch, ...(truncate ? { truncate: true } : {}) });
}

/**
 * 止まった replay を直す（`OcgRematchOps`。Issue #618）。記録済みの応答を全部、選んだカードで付け直して再実行する。
 * apply=false は確かめるだけ（body.replayable = 何手目まで通るか、body.total、body.stopped）。apply=true は通るときだけ書く。
 * dropAfterStop: 付け直しても止まる手から後ろを消して書く
 */
export function rematchOps(replayId, { apply = false, dropAfterStop = false, hold = false } = {}) {
  return api.ocgRematchOps({ replay_id: replayId, apply, drop_after_stop: dropAfterStop, ...holdOf(hold), want_events: true, ...INPUT });
}

/** replay の head を変える（`{ start_video_ms }` / `{ first_player }`。Issue #325） */
export function updateReplay(replayId, patch, { force = false, hold = false } = {}) {
  return api.ocgUpdateReplay({ replay_id: replayId, ...patch, ...forceOf(force), ...holdOf(hold), want_events: true, ...INPUT });
}

/** replay を作る（1 試合 1 replay。先攻は P1、両デッキを取り込む）。replay_id を返す */
export async function createReplayForGame(game, gameUuid) {
  const [deck1, deck2] = deckIdsOfGame(game);
  const r = await api.ocgCreateReplay({ name: `${game?.game_name ?? game?.name ?? 'replay'} ${new Date().toLocaleString()}`, game_uuid: gameUuid, deck1_id: deck1 ?? null, deck2_id: deck2 ?? null, first_player: 0 });
  if (!r?.replay_id) throw new Error(r?.error ?? 'replay を作れませんでした');
  return r.replay_id;
}

/**
 * ログを1からやり直す: その試合の replay を全部消して（操作・登録・先攻と開始位置・タイミング調節の timing は replay の子なので一緒に消える）、
 * デッキを取り込み直した新しい replay を作る。{ deleted, replayId } を返す
 */
export async function resetReplayForGame(gameUuid) {
  const info = await api.project('GetDuelGameInfo', { game_uuid: gameUuid });
  const game = info?.status === 'success' ? info.duel : null;
  if (!game) throw new Error('試合が見つかりません');
  const rows = await listReplays(gameUuid);
  for (const row of rows) await api.ocgDeleteReplay(row.replay_id);   // 1 試合 1 replay なので作る前に全部消す
  const replayId = await createReplayForGame(game, gameUuid);
  return { deleted: rows.length, replayId };
}

/**
 * 修正の会話（Issue #946 / #947。サーバーの OcgFix*）。試合（game_uuid）の replay を引き、会話の状態・発言を返す。
 * どれも失敗は throw（`error.busy`＝考えている最中・`error.stale`＝提案のあとに記録が変わった）。返りは OcgFixState と同じ形
 */
export async function fixReplayId(gameUuid) {
  return latestReplayId(await listReplays(gameUuid));
}

function fixBody(res) {
  if (res?.ok) return res.body;
  const err = new Error(res?.body?.error ?? '修正の会話の呼び出しに失敗しました');
  err.busy = !!res?.body?.busy;
  err.stale = !!res?.body?.stale;
  throw err;
}

export async function fixState(replayId, sinceId = 0) { return fixBody(await api.ocgFixState(replayId, sinceId)); }
export async function fixSend(replayId, text) { return fixBody(await api.ocgFixSend(replayId, text)); }
export async function fixApply(replayId) { return fixBody(await api.ocgFixApply(replayId)); }
export async function fixCancel(replayId, { discardProposal = false } = {}) { return fixBody(await api.ocgFixCancel(replayId, discardProposal)); }
export async function fixReset(replayId) { return fixBody(await api.ocgFixReset(replayId)); }
/** 当てた差分を元に戻す（OcgRevertPatch。当てたあと記録が変わっていなければ） */
export async function revertPatch(replayId, patchId) { return fixBody(await api.ocgRevertPatch(replayId, patchId)); }
